import { Signal } from 'typed-signals';
import { getLocalizedText } from '@softgames/localization';
import type { Popup } from '../../../popups/Popup';
import { LuckyWheelPopupLayout } from '../../../../layouts/luckyWheel/LuckyWheelPopupLayout';
import { dailyJackpotRewards, luckyWheelRewards } from '../LuckyWheelConstants';
import { formatAmountForInventory, setIconForInventory } from '../../../mainMenu/shopPage/utils/setIconForInventory';
import { getDailyJackpotRewardIndex } from '../LuckyWheelUtils';
import { applyLayoutState, revokeLayoutState } from '../../../libs/layouts/populateLayouts/applyLayoutState';
import type { LuckyWheelReward } from '../types';
import type { LuckyWheelRewardWedgeLayout } from '../../../../layouts/luckyWheel/LuckyWheelRewardWedgeLayout';
import { LuckyWheelRewardAnimationLayout } from '../../../../layouts/luckyWheel/LuckyWheelRewardAnimationLayout';
import { LuckyWheelRewardItemLayout } from '../../../../layouts/luckyWheel/LuckyWheelRewardItemLayout';
import { Easing } from '../../../libs/TweenManager/easing';
import { waitForMs } from '../../../utils/async-utils';
import { playLayoutAnimation } from '../../../libs/layouts/layoutAnimations/playLayoutAnimation';
import { interpolationMap } from '../../../libs/TweenManager/interpolations';
import { attachIntervalToDisplayObject, CountDownFormat, formatIntoUnitCountdown } from '../../../utils/time-utils';
import { LuckyWheelLightAnimator } from './LuckyWheelLightAnimator';
import { InventoryItem } from '../../../app/resources/types';
import { soundManager } from '../../../app/SoundManager';
import MathUtils from '../../../utils/MathUtils';

export enum LuckyWheelPopupState {
  FreeSpin = 'FreeSpin',
  AdSpin = 'AdSpin',
  NoSpin = 'NoSpin',
}

interface LuckyWheelPopupConfig {
  state: LuckyWheelPopupState;
  spinCount: number;
  nextSpinTime: number;
}

export class LuckyWheelPopup implements Popup {
  public static readonly requiredLayouts = [LuckyWheelPopupLayout];

  public readonly onCloseClicked = new Signal();

  public readonly onFreeSpinButtonClicked = new Signal();

  public readonly onAdSpinButtonClicked = new Signal();

  public readonly onClaimButtonClicked = new Signal();

  public readonly onSpinResetTimeout = new Signal();

  private readonly layout = new LuckyWheelPopupLayout();

  private readonly wheelAnimator = new LuckyWheelLightAnimator(this.layout);

  private rewardOverlay: LuckyWheelRewardAnimationLayout | null = null;

  private rewardView: LuckyWheelRewardItemLayout | null = null;

  public readonly root = this.layout.root;

  private currentState = LuckyWheelPopupState.NoSpin;

  private cancelTimeUpdate: (() => void) | null = null;

  constructor() {
    const {
      closeButton,
      freeSpinButton: { root: freeSpinButton },
      adSpinButton: { root: adSpinButton },
      blackLayer,
    } = this.layout;

    closeButton.on('pointertap', async () => {
      this.onCloseClicked.emit();
    });

    freeSpinButton.on('pointertap', () => {
      this.onFreeSpinButtonClicked.emit();
    });

    adSpinButton.on('pointertap', () => {
      this.onAdSpinButtonClicked.emit();
    });

    blackLayer.eventMode = 'static';

    void playLayoutAnimation(this.layout, 'Appear', false);
  }

  public refreshView(config: LuckyWheelPopupConfig): void {
    const { jackpotRewardIcon, jackpotRewardAmountText, spinCountText, timerText, sectionsContainer } = this.layout;

    // Reset the wheel angle, since the refresh may happen after a spin
    sectionsContainer.angle = 0;

    const rewardViews = this.getWheelRewardViews();

    const rewardsToShow = luckyWheelRewards.filter((value) => value.reward && !value.isJackpot);

    rewardViews.forEach((view, index) => {
      const { rewardIcon, amountText } = view;
      const reward = rewardsToShow.at(index)?.reward;
      if (reward) {
        setIconForInventory(rewardIcon, reward.resource);
        formatAmountForInventory(amountText, reward.resource, reward.quantity);
      }
    });

    const jackpotReward = dailyJackpotRewards.at(getDailyJackpotRewardIndex());
    if (jackpotReward) {
      setIconForInventory(jackpotRewardIcon, jackpotReward.resource);
      formatAmountForInventory(jackpotRewardAmountText, jackpotReward.resource, jackpotReward.quantity);
    }

    spinCountText.text = getLocalizedText('<amount> left').replace('<amount>', `${config.spinCount}`);

    this?.cancelTimeUpdate?.();

    if (config.state === LuckyWheelPopupState.NoSpin && config.nextSpinTime > 0) {
      this.cancelTimeUpdate = attachIntervalToDisplayObject(
        timerText,
        () => {
          const timeLeft = config.nextSpinTime - Date.now();
          if (timeLeft < 0) {
            this.onSpinResetTimeout.emit();
            return;
          }

          timerText.text = formatIntoUnitCountdown(timeLeft, 2, CountDownFormat.DHM);
        },
        1000,
      );
    }

    revokeLayoutState(this.layout, this.currentState);
    applyLayoutState(this.layout, config.state);

    if (config.spinCount < 2 && config.state === LuckyWheelPopupState.FreeSpin) {
      spinCountText.visible = false;
    }

    this.wheelAnimator.animateIdle();

    this.currentState = config.state;
  }

  public async animateWheelSpinFlow(rewardIndex: number, reward: LuckyWheelReward, isJackpot: boolean): Promise<void> {
    this.enableButtons(false);
    void playLayoutAnimation(this.layout, 'Disappear_Buttons', false);

    this.wheelAnimator.animateSpin();
    await this.animateSpin(rewardIndex);

    this.showRewardOverlay(reward, isJackpot);
  }

  public async animateClaimFlow(newConfig: LuckyWheelPopupConfig): Promise<void> {
    void this.closeRewardOverlay();

    await playLayoutAnimation(this.layout, 'Disappear_Wheel', false);

    this.refreshView(newConfig);

    this.enableButtons(true);
    void playLayoutAnimation(this.layout, 'Appear_Buttons', false);
    await playLayoutAnimation(this.layout, 'Appear_Wheel', false);
  }

  public async animateRefreshFlow(newConfig: LuckyWheelPopupConfig): Promise<void> {
    void playLayoutAnimation(this.layout, 'Disappear_Buttons', false);
    await playLayoutAnimation(this.layout, 'Disappear_Wheel', false);

    this.refreshView(newConfig);

    void playLayoutAnimation(this.layout, 'Appear_Buttons', false);
    await playLayoutAnimation(this.layout, 'Appear_Wheel', false);
  }

  private async animateSpin(rewardIndex: number): Promise<void> {
    const { pointer, sectionsContainer } = this.layout;

    // Wheel spin configs
    const startingSpins = 2;
    const startTweenDuration = 3000;
    const totalStartAngle = 360 * startingSpins;
    const startingBlockerOffset = 22;

    // Pointer configs
    const maxPointerDeflection = 150;
    const blockerTriggerThreshold = 10;
    const pointerDeflectEase = 0.4;
    const pointerReturnEase = 0.15;

    let nextBlockerAngle = startingBlockerOffset;
    let previousSectionAngle = sectionsContainer.angle;
    const handlePointerUpdate = (): void => {
      const distanceToBlocker = Math.max(0, nextBlockerAngle - sectionsContainer.angle);

      const angularDelta = sectionsContainer.angle - previousSectionAngle;
      const pointerDirection = angularDelta > 0 ? -1 : 1;

      const blockerInfluence = Math.max(0, 1 - distanceToBlocker / blockerTriggerThreshold);
      const speedInfluence = Math.min(Math.abs(angularDelta) / 10, 1);

      const targetPointerAngle =
        blockerInfluence > 0 ? pointerDirection * maxPointerDeflection * blockerInfluence * speedInfluence : 0;
      const ease = targetPointerAngle < pointer.angle ? pointerDeflectEase : pointerReturnEase;

      pointer.angle += (targetPointerAngle - pointer.angle) * ease;

      if (sectionsContainer.angle > nextBlockerAngle) {
        soundManager.play('Diorama_Hit');
        nextBlockerAngle += 45;
      }

      previousSectionAngle = sectionsContainer.angle;
    };

    const wheelStartTween = sectionsContainer.tweenTo(
      { angle: totalStartAngle },
      startTweenDuration,
      Easing.Sinusoidal.In,
      true,
    );
    wheelStartTween.onUpdate.add(handlePointerUpdate);

    await wheelStartTween.asyncOnComplete();

    // Reset the angles now that the wheel has done the starting spins
    sectionsContainer.angle = 0;
    previousSectionAngle = 0;
    nextBlockerAngle = startingBlockerOffset;

    // Calculate the angle of the wedge by the given reward
    const rewardAngle = 360 - rewardIndex * 45 + MathUtils.getRandomValueInRange(-20, 20);

    // For the stopping sequence do an entire spin plus the angle relative to the given reward
    const targetAngle = 360 + rewardAngle;

    const startTweenVelocity = totalStartAngle / startTweenDuration;
    const endTweenDuration = targetAngle / startTweenVelocity;
    const wheelEndTween = sectionsContainer.tweenTo(
      { angle: targetAngle },
      endTweenDuration,
      Easing.Sinusoidal.Out,
      true,
    );
    wheelEndTween.onUpdate.add(handlePointerUpdate);

    const promises: Promise<void>[] = [];

    promises.push(this.wheelAnimator.animateStop(endTweenDuration));
    promises.push(wheelEndTween.asyncOnComplete());

    await Promise.all(promises);

    pointer.tweenTo({ angle: 0 }, 100, 'quadIn', true, endTweenDuration);

    soundManager.play('UI_Win_Screen');
    await waitForMs(800);
    soundManager.play('Gameplay_Match_3');

    this.wheelAnimator.animateReward();

    await waitForMs(500);

    /*
    // This could be used when stopping the wheel near a pin
    const wheelBackTween = sectionsContainer.tweenTo(
      { angle: sectionsContainer.angle - 5 },
      1500,
      Easing.Cubic.InOut,
      true,
    );
    wheelBackTween.onUpdate.add(handlePointerUpdate);
    await wheelBackTween.asyncOnComplete();
    */
  }

  private showRewardOverlay(reward: LuckyWheelReward, isJackpot: boolean): void {
    this.rewardOverlay = new LuckyWheelRewardAnimationLayout();
    const {
      root: overlayRoot,
      rewardContainer,
      jackpotContainer,
      flashContainer,
      sparkleJackpot,
      claimButton: { root: claimButton },
    } = this.rewardOverlay;

    overlayRoot.props({
      parent: this.layout.root,
    });

    if (!isJackpot) {
      jackpotContainer.props({
        visible: false,
      });

      flashContainer.props({
        visible: false,
      });

      sparkleJackpot.props({
        visible: false,
      });
    }

    claimButton.on('pointertap', () => {
      this.onClaimButtonClicked.emit();
    });

    this.rewardView = new LuckyWheelRewardItemLayout();

    const { root: rewardRoot, wonRewardAmountText, wonRewardIcon } = this.rewardView;

    rewardRoot.props({
      parent: this.root.pixi.stage,
      position: rewardContainer.getGlobalPosition(),
      scaleXY: 0,
    });

    // Using bigger icons since default item icons are quite smaller than what the reward overlay expects
    const iconOverrides = {
      [InventoryItem.Lives]: 'Heart_Big_New',
      [InventoryItem.UnlimitedLivesMinutes]: 'Reward_Infinite_Lives_Big',
    };

    setIconForInventory(wonRewardIcon, reward.resource, iconOverrides);
    formatAmountForInventory(wonRewardAmountText, reward.resource, reward.quantity);

    void playLayoutAnimation(this.rewardOverlay, 'Appear', false);

    this.rewardView.root.tweenTo({ scaleX: [1.1, 1], scaleY: [1.1, 1] }, 350, 'quadInOut', true, 70);
  }

  private async closeRewardOverlay(): Promise<void> {
    if (!this.rewardOverlay || !this.rewardView) return;

    const {
      root: overlayRoot,
      rewardContainer,
      claimButton: { root: claimButton },
    } = this.rewardOverlay;

    const { root: rewardRoot } = this.rewardView;

    claimButton.eventMode = 'none';

    await this.rewardView.wonRewardAmountText.tweenTo({ alpha: 0 }, 150, 'quadInOut', true).asyncOnComplete();

    void rewardRoot.tweenTo({ angle: -40, scaleX: 0.1, scaleY: 0.1 }, 900, 'quadIn', true);

    const tweenProps = {
      x: [rewardContainer.x, -400],
      y: [rewardContainer.y, 100],
    };

    void rewardRoot
      .tweenTo(tweenProps, 900, 'cubicIn', false)
      .interpolation(interpolationMap.bezier)
      .start()
      .asyncOnComplete();

    await playLayoutAnimation(this.rewardOverlay, 'Disappear', false);
    overlayRoot.destroy({ children: true });
    rewardRoot.destroy({ children: true });

    this.rewardOverlay = null;
  }

  private enableButtons(value: boolean): void {
    const {
      closeButton,
      freeSpinButton: { root: freeSpinButton },
      adSpinButton: { root: adSpinButton },
    } = this.layout;

    closeButton.eventMode = value ? 'static' : 'none';
    freeSpinButton.eventMode = value ? 'static' : 'none';
    adSpinButton.eventMode = value ? 'static' : 'none';
  }

  private getWheelRewardViews(): LuckyWheelRewardWedgeLayout[] {
    return [
      this.layout.reward1,
      this.layout.reward2,
      this.layout.reward3,
      this.layout.reward4,
      this.layout.reward5,
      this.layout.reward6,
      this.layout.reward7,
    ];
  }
}
